import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import dayjs from 'dayjs';
import * as Clipboard from 'expo-clipboard';
import { Formik } from 'formik';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Modal,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from 'react-native';
import Toast, { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import CustomTabBar from '@components/CustomTabBar/CustomTabBar';
import { ModalHeader } from '@components/Headers/ModalHeader';
import {
  AddMemberType,
  SelectMembersList,
} from '@components/Invite/SelectMembersList';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { WebModal } from '@components/Modals/WebModal';
import { Box, ShadowBox, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import ListNavigationItem from '@components/shared/ListNavigationItem/ListNavigationItem';
import Tag from '@components/shared/Tags/Tag';
import { TeamExpiryBanner } from '@components/Team/TeamExpiryBanner.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { PopupTeamMemberRoleButtonweb } from '@components/Web/PopupTeamMemberRoleButton.web';
import {
  GetTeamDetailDocument,
  Invoice,
  LocalFile,
  Maybe,
  MemberAttributes,
  Payment,
  Scalars,
  Team,
  TeamMemberRole,
  TeamRole,
  useCreateTeamInvitationMutation,
  User,
  useRemoveTeamMemberMutation,
  useUpdateTeamMembersMutation,
} from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import useMe from '@hooks/useMe';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { projectFormSchema } from '@schemas/projectFormSchema';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import {
  defaultTeamRole,
  getTeamRoleEnumValue,
  selectableTeamRoles,
} from '@utils/teams';
import { getTeamLabel } from '@utils/teams/getTeamLabel';
import { getUpgradeCTATitleAndDesc } from '@utils/teams/subscription';

export const storageUsedColor = (
  storageAvailable: number,
  totalSizeAvaiblePlan: number
) => {
  if (storageAvailable <= 0) return 'alertRed';
  if (storageAvailable < totalSizeAvaiblePlan * 0.1) return 'yellow';
  return 'greenSecondary';
};

type PlanUpgradeType = {
  title: string;
  description: string;
  iconName: 'Zap';
  iconColor: string;
  iconBackgroundColor: string;
  onPress: () => void;
};

const Tab = createMaterialTopTabNavigator();

export const SubscriptionPlanDetail = ({ route }) => {
  const TASKTAG_SUPPORT_EMAIL = 'emmanueln@lovettcommercial.com';
  const {
    params: { teamUUId },
  } = route;

  const navigation = useAppNavigation();

  const { me } = useMe();

  const { t } = useTranslation();
  const { t: f } = useTranslation('models');
  const { setSelectedTeam, selectedTeam } = useWebDrawerNavigator();
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const [planUpgrade, setPlanUpgrade] = useState<PlanUpgradeType | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isDeleteModalVisible, setDeleteModalVisible] =
    useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<User | undefined>(
    undefined
  );

  const [showUpgradeOrPaymentMethodCTA, setShowUpgradeOrPaymentMethodCTA] =
    useState<boolean>(false);

  const { contacts } = useContacts();

  const {
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setIsTeamPersonal,
    setTeamValues,
    teamValues,
    setUserInvited,
    setUpgradeConfirmModalOpen,
  } = useWebDrawer();

  const offsetPopButton: Partial<any> = {
    position: 'left',
    offset: [0, 6],
  };
  const teamRoles: { key: TeamRole | 'all'; str: string }[] = [
    {
      key: 'all',
      str: t('models:profile.subscriptionPlans.allRoles'),
    },
    { key: TeamRole.Admin, str: t('models:profile.subscriptionPlans.admin') },
    {
      key: TeamRole.Member,
      str: t('models:profile.subscriptionPlans.member'),
    },
  ];

  useEffect(() => {
    const unsubscribe = navigation
      .getParent()
      ?.addListener('drawerItemPress', (e) => {
        setSelectedTeam(undefined);
        navigation.reset({ routes: [{ name: 'tabs' }], index: 0 });
        e.preventDefault();
      });

    return unsubscribe;
  }, [navigation]);

  // getPublicTeam, not getTeam (TA-10850): getTeam answers "The requested team
  // could not be found" for a viewer who is not a member of the team yet.
  const { fullTeam: cacheTeam } = useTeamDetailFromQuery({
    teamUUId,
    fetchPolicy: 'cache-only',
    includePending: true,
  });

  const { fullTeam: team, refetch } = useTeamDetailFromQuery({
    teamUUId,
    fetchPolicy: 'network-only',
    includePending: true,
  });

  const [teamId, setTeamId] = useState<string>('');

  const { idTeamDelete, setIdTeamDelete } = useWebDrawer();

  useEffect(() => {
    if (!team?.subscriptionDetails && idTeamDelete !== '') {
      setIdTeamDelete('');
      navigation.navigate('tabs', {
        screen: 'my-profile',
      });
    }
  }, [idTeamDelete]);

  useEffect(() => {
    team?.id && setTeamId(team?.id);
  }, [team]);

  const totalSizeAvaiblePlan = team
    ? Math.max(0, parseInt(team?.maxDocumentStorage))
    : 0;

  const storageAvailable = team
    ? Math.max(0, parseInt(team?.storageRemaining))
    : 0;

  const [_hasPaymentMethod, setHasPaymentMethod] = useState(false);
  useEffect(() => {
    if (
      team?.subscriptionDetails?.paymentCardLast4 !== null ||
      team?.subscriptionDetails?.paymentCardLast4 !== undefined
    ) {
      setHasPaymentMethod(true);
    } else {
      setHasPaymentMethod(false);
    }
  }, [team?.subscriptionDetails?.paymentCardLast4]);

  function checkRemainingDays(targetDateStr: string | number | Date) {
    const targetDate = new Date(targetDateStr);
    const currentDate = new Date();

    const currentUTC = new Date(
      Date.UTC(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth(),
        currentDate.getUTCDate()
      )
    );

    const targetUTC = new Date(
      Date.UTC(
        targetDate.getUTCFullYear(),
        targetDate.getUTCMonth(),
        targetDate.getUTCDate()
      )
    );

    const diffInMs = targetUTC.getTime() - currentUTC.getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

    if (!isNaN(diffInDays)) return diffInDays;

    return undefined;
  }

  const [isExpired, setIsexpired] = useState(false);
  useEffect(() => {
    const dateRemaining = checkRemainingDays(
      team?.subscriptionDetails?.expirationDate
    );

    if (
      team?.subscriptionDetails?.isSubscriptionCanceled &&
      dateRemaining &&
      dateRemaining <= 0
    ) {
      setIsexpired(true);
      return;
    }
    if (
      team?.subscriptionDetails?.expirationDate &&
      dateRemaining &&
      dateRemaining <= 7 &&
      (team?.subscriptionDetails?.paymentCardLast4 === null ||
        team?.subscriptionDetails?.paymentCardLast4 === undefined)
    ) {
      setIsexpired(true);
    }
  }, [
    team?.subscriptionDetails?.expirationDate,
    team?.subscriptionDetails?.isSubscriptionCanceled,
  ]);

  useEffect(() => {
    if (!selectedTeam && team) {
      setSelectedTeam(team);
    } else if (selectedTeam && team && selectedTeam.id === team.id) {
      setSelectedTeam(team);
    }
  }, [cacheTeam?.name, team?.name, team?.avatarFile]);

  const toastRef = useRef();
  const [updateTeamMembers, { data: updateTeamMembersData }] =
    useUpdateTeamMembersMutation({
      refetchQueries: teamUUId
        ? [
            {
              query: GetTeamDetailDocument,
              variables: { id: teamUUId, includePending: true },
            },
          ]
        : [],
    });

  useEffect(() => {
    if (!updateTeamMembersData) return;
    toastRef?.current?.show(
      t('models:profile.subscriptionPlans.theUserHasBeenAddedToYourTeamPlan'),
      { duration: 2000 }
    );
  }, [updateTeamMembersData]);

  const [getLabel, setGetLabel] = useState<string>(getTeamLabel(team));
  let planStatusLabelColor =
    getLabel === 'Renews' || getLabel === 'Free Forever'
      ? 'greenSecondary'
      : 'alertRed';
  useEffect(() => {
    setGetLabel(getTeamLabel(team));
    planStatusLabelColor =
      getLabel === 'Renews' || getLabel === 'Free Forever'
        ? 'greenSecondary'
        : 'alertRed';
  }, [team?.subscriptionDetails]);

  const goToUpgradeSubscription = () => {
    setIsTaskWebPanelOpen(false);

    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setTeamValues(team);
    setUpgradeConfirmModalOpen(true);
  };

  const goToUpgradeSubscriptionWithInvite = () => {
    setIsTaskWebPanelOpen(false);
    setUserInvited(true);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setTeamValues(team);
    setUpgradeConfirmModalOpen(true);
  };

  const updateTeamMember = async (userId: User['id'], role: TeamRole) => {
    await updateTeamMembers({
      variables: {
        teamId,
        attributes: {
          userId: userId,
          role: getTeamRoleEnumValue(role),
        } as MemberAttributes,
      },
      onCompleted: () => {},
      refetchQueries: teamUUId
        ? [
            {
              query: GetTeamDetailDocument,
              variables: { id: teamUUId, includePending: true },
            },
          ]
        : [],
    });
  };

  const teamRolePriority = (m: User) => {
    if (m.id === team?.owner?.id) return 1;
    if ((m.teamRoles || []).includes(TeamRole.Admin)) return 0;
    return -1;
  };

  const initialValues = {
    users: [],
    selectedUserIds: (team?.users || []).map((u) => u.id),
  };

  const members = [...(team?.users || [])].sort(
    (m1, m2) => teamRolePriority(m2) - teamRolePriority(m1)
  );
  const isOwner = team?.owner?.id === me?.id;

  const isAdmin = members.some(
    (m) => m.id == me?.id && m.teamRoles?.includes(TeamRole.Admin)
  );

  const isCanceled = team?.subscriptionDetails?.isSubscriptionCanceled ?? false;
  const isPersonal = team?.personal;

  const canRemoveMember =
    team?.subscriptionDetails &&
    !(team?.personal ?? false) &&
    !isCanceled &&
    (isOwner || isAdmin);

  const roleDisplayStr = (role: TeamRole) => {
    switch (role) {
      case TeamRole.Owner:
        return 'Owner';
      case TeamRole.Admin:
        return 'Admin';
      default:
        return 'Member';
    }
  };

  const sortInvoices = (sortUP: boolean) => {
    const number = sortUP ? 1 : -1;
    return [...(team?.invoices || [])].sort(
      (m1, m2) => (dayjs(m2.date).isAfter(dayjs(m1.date)) ? -1 : 1) * number
    );
  };
  const [invoicesSortByDateUp, setInvoicesSortByDateUp] =
    useState<boolean>(false);
  const [displayInvoices, setDisplayInvoices] = useState<Invoice[]>(
    sortInvoices(invoicesSortByDateUp)
  );

  const [hoverInvoiceId, setHoverInvoiceId] = useState<string | undefined>();
  const [displayMembers, setDisplayMembers] = useState(members);

  const [hoverMemberId, setHoverMemberId] = useState<string | undefined>();

  const [selectedRole, setSelectedRole] = useState<string | undefined>(
    teamRoles[0].key
  );
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [isRefreshingInvoices, setIsRefreshingInvoices] = useState(false);
  const delayedRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const previousTeamMemberCountRef = useRef<number | null>(null);

  const [removeTeamMember] = useRemoveTeamMemberMutation();
  const toast = useToast();
  const maxTitleWidth = 340;

  const resetMembers = (role: TeamRole | 'all') => {
    setDisplayMembers(
      (role === 'all'
        ? members
        : members.filter((u) =>
            (u.teamRoles || []).includes(role as TeamRole)
          )) || []
    );
  };

  const refetchInvoicesWithDelay = useCallback(() => {
    if (delayedRefetchTimeoutRef.current) {
      clearTimeout(delayedRefetchTimeoutRef.current);
    }

    setIsRefreshingInvoices(true);

    const timeout = setTimeout(async () => {
      try {
        await refetch();
      } finally {
        setIsRefreshingInvoices(false);
        delayedRefetchTimeoutRef.current = null;
      }
    }, 3000);

    delayedRefetchTimeoutRef.current = timeout;
  }, [refetch]);

  useEffect(() => {
    resetMembers(selectedRole as TeamRole | 'all');
  }, [team?.users]);

  useEffect(() => {
    setDisplayInvoices(sortInvoices(invoicesSortByDateUp));
  }, [team?.invoices?.length, team?.invoices, invoicesSortByDateUp]);

  useEffect(() => {
    const currentMemberCount = team?.users?.length || 0;

    if (
      previousTeamMemberCountRef.current !== null &&
      currentMemberCount > previousTeamMemberCountRef.current
    ) {
      refetchInvoicesWithDelay();
    }

    previousTeamMemberCountRef.current = currentMemberCount;
  }, [team?.users?.length, refetchInvoicesWithDelay]);

  useEffect(() => {
    return () => {
      if (delayedRefetchTimeoutRef.current) {
        clearTimeout(delayedRefetchTimeoutRef.current);
      }
    };
  }, []);

  const renderHeader = () => {
    const titleText = (title: string) => (
      <Text variant='metadataSecondary' color='grey04'>
        {title.toUpperCase()}
      </Text>
    );
    return (
      <Box
        flexDirection='row'
        height={40}
        backgroundColor='grey01'
        borderRadius='xs'>
        <Box
          flex={1}
          flexDirection='row'
          style={{ paddingHorizontal: 20 }}
          justifyContent='flex-start'
          alignItems='center'>
          {titleText('Date')}
          <Icon
            name='Sort2'
            color='grey02'
            variant='l'
            marginLeft='xxs'
            marginTop='s'
            onPress={() => {
              setDisplayInvoices(sortInvoices(!invoicesSortByDateUp));
              setInvoicesSortByDateUp(!invoicesSortByDateUp);
            }}
          />
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          {titleText('Total')}
        </Box>
        <Box flex={1.5} px='m' justifyContent='center'>
          {titleText('Description')}
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          {titleText('status')}
        </Box>
        <Box width={67}></Box>
      </Box>
    );
  };

  const renderItem = (item: {
    __typename: 'Invoice';
    amount: Scalars['Int'];
    date: Scalars['ISO8601DateTime'];
    description: Scalars['String'];
    id: Scalars['ID'];
    invoiceUrl: Scalars['String'];
    payment?: Maybe<Payment>;
    status: Scalars['String'];
  }) => {
    return (
      <Box
        flexDirection='row'
        height={38}
        style={{
          borderBottomColor: theme.colors.grey02,
          borderBottomWidth: 1,
        }}>
        <Box flex={1} px='m' justifyContent='center'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {dayjs(item.date).format('MMMM DD, YYYY')}
          </Text>
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {`$${(item.amount / 100).toFixed(2)}`}
          </Text>
        </Box>
        <Box flex={1.5} px='m' justifyContent='center'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {item.description}
          </Text>
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {item.status}
          </Text>
        </Box>
        <Box
          px='m'
          justifyContent='center'
          onMouseEnter={() => {
            setHoverInvoiceId(item.id);
          }}
          onMouseLeave={() => {
            setHoverInvoiceId(undefined);
          }}>
          <TouchableOpacity
            onPress={() => {
              item.invoiceUrl && window.open(item.invoiceUrl, '_blank');
            }}>
            <Text
              variant='webBodySecondary'
              color='greenSecondary'
              textDecorationLine={
                hoverInvoiceId === item.id ? 'underline' : undefined
              }>
              {t('shared:view')}
            </Text>
          </TouchableOpacity>
        </Box>
      </Box>
    );
  };

  const renderMembersHeader = () => {
    const titleText = (title: string) => (
      <Text variant='metadataSecondary' color='grey04'>
        {title.toUpperCase()}
      </Text>
    );
    return (
      <Box
        flexDirection='row'
        height={40}
        backgroundColor='grey01'
        borderRadius='xs'>
        <Box
          flex={1.5}
          flexDirection='row'
          style={{ paddingHorizontal: 20 }}
          justifyContent='flex-start'
          alignItems='center'>
          {titleText('Name')}
          {/*<Icon*/}
          {/*  name='Sort2'*/}
          {/*  color='grey02'*/}
          {/*  variant='l'*/}
          {/*  marginLeft='xxs'*/}
          {/*  marginTop='s'*/}
          {/*/>*/}
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          {titleText('Role')}
        </Box>
        <Box flex={1.5} px='m' justifyContent='center'>
          {titleText('Email')}
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          {titleText('Phone')}
        </Box>
        {canRemoveMember && <Box width={100}></Box>}
      </Box>
    );
  };

  const formatPhoneNumber = (xphoneNumber: string) => {
    const phoneNumber = xphoneNumber.replace(/\D/g, '');
    if (phoneNumber.startsWith('1')) {
      return phoneNumber.length == 11
        ? phoneNumber.substring(1, 4) +
            '-' +
            phoneNumber.substring(4, 7) +
            '-' +
            phoneNumber.substring(7)
        : phoneNumber.substring(0, 3) +
            '-' +
            phoneNumber.substring(3, 6) +
            '-' +
            phoneNumber.substring(6);
    } else {
      return phoneNumber;
    }
  };

  const removeMember = (item: User) => {
    removeTeamMember({
      variables: {
        id: teamId,
        userId: item.id,
      },
      onCompleted: () => {
        setDeleteModalVisible(false);
      },
      refetchQueries: [
        ...(teamUUId
          ? [
              {
                query: GetTeamDetailDocument,
                variables: { id: teamUUId, includePending: true },
              },
            ]
          : []),
      ],
    });
  };

  const renderMemberItem = (item: User) => {
    return (
      <Box
        flexDirection='row'
        height={48}
        style={{
          borderBottomColor: theme.colors.grey02,
          borderBottomWidth: 1,
        }}>
        <Box
          flex={1.5}
          px='m'
          justifyContent='flex-start'
          alignItems='center'
          flexDirection='row'>
          <Avatar
            id={item.id}
            avatar={item.avatar}
            size='small'
            label={item.name}
          />
          <Text variant='webBodySecondary' color='textPrimary' ml='xs'>
            {item.name}
          </Text>
        </Box>
        {/*<Box flex={1} px='m' justifyContent='center'>*/}
        {/*  <Text variant='webBodySecondary' color='textPrimary'>*/}
        {/*    // here need read from backend*/}
        {/*    Recently*/}
        {/*  </Text>*/}
        {/*</Box>*/}
        <Box
          flex={1}
          px='m'
          justifyContent='flex-start'
          alignItems='center'
          flexDirection='row'>
          <TouchableOpacity
            key={item.id}
            onPress={() => {
              if (
                (isOwner || isAdmin) &&
                item.id !== me?.id &&
                !item.teamRoles?.includes(TeamRole.Owner)
              ) {
                setSelectedMember(item);
                setModalOpen(true);
              }
            }}
            accessibilityLabel={item.name ?? ''}>
            <Box
              flex={1}
              justifyContent='flex-start'
              alignItems='center'
              flexDirection='row'>
              <Text variant='webBodySecondary' color='textPrimary'>
                {item.id === team?.owner?.id
                  ? roleDisplayStr(TeamMemberRole.Owner)
                  : (item.teamRoles || []).length > 0 &&
                    roleDisplayStr(item.teamRoles![0])}
              </Text>
              {(isOwner || isAdmin) &&
                item.id !== me?.id &&
                !item.teamRoles?.includes(TeamRole.Owner) && (
                  <Icon
                    name='ChevronDown'
                    color='textPrimary'
                    variant='s'
                    marginLeft='xs'
                  />
                )}
            </Box>
          </TouchableOpacity>
        </Box>
        <Box flex={1.5} px='m' justifyContent='center'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {item.email}
          </Text>
        </Box>
        <Box flex={1} px='m' justifyContent='center'>
          <Text variant='webBodySecondary' color='textPrimary'>
            {formatPhoneNumber(item.phoneNumber)}
          </Text>
        </Box>
        {!canRemoveMember && <Box width={100} />}
        {canRemoveMember && (
          <Box
            width={100}
            px='m'
            justifyContent='center'
            onMouseEnter={() => {
              setHoverMemberId(item.id);
            }}
            onMouseLeave={() => {
              setHoverMemberId(undefined);
            }}>
            {item.id !== me?.id && (
              <TouchableOpacity
                onPress={() => {
                  setSelectedMember(item);
                  setDeleteModalVisible(true);
                }}>
                <Text
                  variant='webBodySecondary'
                  color='alertRed'
                  textDecorationLine={
                    hoverMemberId === item.id ? 'underline' : undefined
                  }>
                  {t('shared:remove')}
                </Text>
              </TouchableOpacity>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const maskEmail = (email: string) => {
    if (!email.includes('@')) return '';
    const [localPart, domain] = email.split('@');

    if (domain.length < 10) {
      return `${localPart[0]}***@${domain}`;
    } else {
      return `${localPart[0]}***@${domain.substring(
        0,
        3
      )} ... ${domain.substring(domain.length - 5)}`;
    }
  };

  const planIcon = () => {
    const team = selectedTeam;
    if (team?.avatarFile)
      return (
        <Box marginHorizontal='xs'>
          <CachedImage
            avatarId={team.avatarFile.id}
            avatarType='team'
            image={team.avatarFile as unknown as LocalFile}
            width={48}
            height={48}
            borderRadius={theme.spacing.xxs}
          />
        </Box>
      );
    if (!team?.avatarFile && team?.personal)
      return (
        <Box
          marginHorizontal='xs'
          width={48}
          height={48}
          backgroundColor='greenSecondaryMild'
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'>
          <Icon variant='l' name='Office' />
        </Box>
      );
    else
      return (
        <Box
          marginHorizontal='xs'
          width={48}
          height={48}
          backgroundColor='black'
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'>
          <Icon name='TT48' width={48} height={48} />
        </Box>
      );
  };

  const getUserRole = (item: Team) => {
    if (item?.owner?.id === me?.id) return TeamMemberRole.Owner;
    return item?.currentUserRole;
  };
  const teamExpirationDate = team?.subscriptionDetails?.isSubscriptionCanceled
    ? team?.subscriptionDetails?.canceledAt
    : team?.subscriptionDetails?.expirationDate;

  const infoPart = () => {
    return (
      <>
        <Box
          flexDirection='row'
          marginBottom='l'
          marginTop='xs'
          alignItems='center'>
          {planIcon()}
          <Text
            accessibilityLabel='Team Name'
            variant='heading2'
            numberOfLines={1}
            style={{ maxWidth: maxTitleWidth }}>
            {team?.name}
          </Text>
          <Box alignItems='center' marginStart='xs' marginTop='xs'>
            {selectedTeam?.id === me?.primaryTeamId && (
              <Icon variant='l' color='textSecondary' name='StarYellow' />
            )}
          </Box>
        </Box>

        <Box
          flexDirection='row'
          justifyContent='space-between'
          marginBottom='l'>
          <Text variant='labelEmphasized' color='black' mr='m'>
            {f('teams.details.location')}
          </Text>
          <Box flex={1}></Box>
          <Text variant='body' color='textSecondary'>
            {team?.address}
          </Text>
        </Box>
        <Box
          flexDirection='row'
          justifyContent='space-between'
          marginBottom='l'>
          <Text variant='labelEmphasized' color='black' mr='m'>
            {f('teams.details.myRole')}
          </Text>
          <Text variant='body' color='textSecondary'>
            {capitalizeFirstLetter(getUserRole(team))}
          </Text>
        </Box>
        <Box>
          <Text variant='labelEmphasized' color='black' mr='m'>
            {f('teams.details.skills')}
          </Text>
          <Box flexDirection='row' flexWrap='wrap'>
            {team?.skills?.map((t) => (
              <Tag
                key={t.id}
                variant='textSecondary'
                label={t.name}
                labelVariant='labelSmall'
                padding='xxs'
                borderRadius='xxs'
                marginRight='xxs'
                color='textSecondary'
                marginTop='xxs'
              />
            ))}
          </Box>
        </Box>
      </>
    );
  };

  let planName = '-';

  if (team?.subscriptionDetails?.subscriptionPlanName) {
    planName = team?.subscriptionDetails?.subscriptionPlanName;
  } else if (
    team?.personal &&
    team?.subscriptionDetails?.subscriptionPlanName == null
  ) {
    planName = 'Team';
  }

  const [createTeamInvitationMutation, { data: createTeamInvitationData }] =
    useCreateTeamInvitationMutation();

  useEffect(() => {
    const inviteLink = createTeamInvitationData?.createTeamInvitation;
    if (!inviteLink) return;
    const formattedLink =
      inviteLink.replace('register/register-with-email', 'invited-user') +
      `&type=team`;
    copyToClipboard(formattedLink);
  }, [createTeamInvitationData]);

  const handleInviteToTeamPress = async () => {
    await createTeamInvitationMutation({
      variables: { id: teamId },
    });
  };

  const teamUsers = team?.users.filter((i) => i.id !== team?.owner?.id);

  const copyToClipboard = async (link: string) => {
    if (!link) return;
    const inviteLink = `You’re invited to join our new team at ${team?.name}. Click the link below to get started: `;
    await Clipboard.setStringAsync(inviteLink + link);
    navigator.share({
      title: inviteLink,
      url: link,
    });
    toast.show('Copied', {
      duration: 2000,
    });
  };

  useEffect(() => {
    if (team) {
      // No upgrade CTA for personal project teams
      if (team?.personal) {
        setShowUpgradeOrPaymentMethodCTA(false);
        return;
      }

      const status = getUpgradeCTATitleAndDesc(team);
      if (status?.titleInTeamDetail) {
        const planUpgrade = {
          title: status?.titleInTeamDetail ?? '',
          description: status?.description ?? '',
          iconName: 'Zap' as const,
          iconColor: 'greenSecondary',
          iconBackgroundColor: 'greenSecondaryMild',
          onPress: goToUpgradeSubscription,
        };
        setPlanUpgrade(planUpgrade);
        setShowUpgradeOrPaymentMethodCTA(true);
        return;
      }
    }
    setShowUpgradeOrPaymentMethodCTA(false);
  }, [team, showUpgradeOrPaymentMethodCTA]);

  const paymentCardBrand = (
    team?.subscriptionDetails?.paymentCardBrand ?? ''
  ).toLowerCase();

  return (
    <Box width='100%' height='100%'>
      <Box flexDirection='row' mx='l' mt='l' gap='m'>
        <Box
          flex={1}
          borderRadius='xs'
          borderWidth={1}
          borderColor='grey02'
          px='m'
          py='s'>
          <Box paddingBottom='m'>
            {infoPart()}
            {showUpgradeOrPaymentMethodCTA && isOwner && (
              <Box
                paddingVertical='xs'
                paddingHorizontal='xs'
                borderRadius='xs'
                marginTop='xs'
                borderWidth={1}
                borderColor='grey02'>
                <ListNavigationItem {...planUpgrade} />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          flex={1}
          borderWidth={1}
          borderColor='grey02'
          borderRadius='xs'
          backgroundColor='white'
          px='l'
          py='l'>
          <Box marginBottom='l'>
            <Text variant='labelEmphasized' fontSize={18}>
              Billing
            </Text>
          </Box>
          <Box
            borderRadius='xs'
            borderWidth={1}
            borderColor='grey02'
            px='l'
            py='l'
            backgroundColor='white'>
            <Box py='xs' flexDirection='row' alignItems='center'>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:profile.subscriptionPlans.plan')}
              </Text>
              <Box flex={1}></Box>
              <Box flexDirection='row' alignItems='center'>
                {team?.personal ? (
                  <Text
                    variant='webBodySecondary'
                    color='textSecondary'
                    ml='xs'>
                    Personal Projects
                  </Text>
                ) : (
                  <Text
                    variant='webBodySecondary'
                    color='textSecondary'
                    ml='xs'>
                    {planName +
                      (team?.personal &&
                      dayjs(team?.createdAt).add(1, 'month') <= dayjs()
                        ? ' (Trial)'
                        : '')}
                  </Text>
                )}
              </Box>
            </Box>

            <Box py='xs' flexDirection='row' justifyContent='flex-start'>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:profile.subscriptionPlans.planStatus')}
              </Text>
              <Box flex={1}></Box>
              <Box flexDirection='column' alignItems='flex-end'>
                <Text variant='webBodySecondary' color={planStatusLabelColor}>
                  {team?.personal || !team?.subscriptionDetails
                    ? getLabel
                    : `${getLabel} on ${dayjs(teamExpirationDate).format(
                        'MMM DD, YYYY'
                      )}`}
                </Text>
                {team?.subscriptionDetails?.isSubscriptionCanceled && (
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    marginTop='xxs'>
                    {`You'll keep access until ${dayjs(
                      team?.subscriptionDetails?.expirationDate
                    ).format('MMM DD, YYYY')}`}
                  </Text>
                )}
              </Box>
            </Box>
            <Box py='xs' flexDirection='row' justifyContent='flex-start'>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:profile.subscriptionPlans.storageUsed')}
              </Text>
              <Box flex={1}></Box>
              <Box flexDirection='row' alignItems='center'>
                {totalSizeAvaiblePlan > 0 && (
                  <Text
                    variant='webBodySecondary'
                    color={storageUsedColor(
                      storageAvailable,
                      totalSizeAvaiblePlan
                    )}>
                    {t('format:fileSize', {
                      val: totalSizeAvaiblePlan - storageAvailable,
                    })}{' '}
                    / {t('format:fileSize', { val: totalSizeAvaiblePlan })}
                  </Text>
                )}
              </Box>
            </Box>
            <Box py='xs' flexDirection='row' justifyContent='flex-start'>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:teams.subscriptions.totalTeamMembers')}
              </Text>
              <Box flex={1}></Box>
              <Box flexDirection='row' alignItems='center'>
                <Text variant='webBodySecondary' color='textSecondary'>
                  {team?.usersCount ?? 0}
                </Text>
              </Box>
            </Box>
            <Box py='xs' flexDirection='row' justifyContent='flex-start'>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:teams.subscriptions.totalAmount')}
              </Text>
              <Box flex={1}></Box>
              <Box flexDirection='row' alignItems='center'>
                <Text variant='webBodySecondary' color='textSecondary'>
                  {team?.subscriptionDetails?.pricePerSeat
                    ? `$${(
                        (Number(team?.subscriptionDetails?.pricePerSeat) /
                          100) *
                        (team?.subscriptionDetails?.currentSeatCount ?? 1)
                      ).toFixed(2)}`
                    : '-'}
                </Text>
              </Box>
            </Box>
            <Box
              py='xs'
              flexDirection='row'
              justifyContent='flex-start'
              height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
              <Text variant='labelSmall' color='textPrimary'>
                {t('models:profile.subscriptionPlans.paymentMethod')}
              </Text>
              <Box flex={1}></Box>
              {/*about link payment brand is email address.*/}
              {team?.subscriptionDetails &&
                (team?.subscriptionDetails.paymentCardBrand ?? '').includes(
                  '@'
                ) && (
                  <Box flexDirection='row' alignItems='center'>
                    <Icon name='LinkPayment' variant='m' color='white' />
                    <Text
                      ml='xs'
                      variant='webBodySecondary'
                      color='textSecondary'
                      numberOfLines={1}>
                      {maskEmail(
                        team?.subscriptionDetails.paymentCardBrand ?? ''
                      )}
                    </Text>
                  </Box>
                )}
              {team?.subscriptionDetails?.paymentCardLast4 ? (
                <Box flexDirection='row' alignItems='center'>
                  {paymentCardBrand === 'visa' && (
                    <Icon name='Visa' variant='xl' color='white' />
                  )}
                  {paymentCardBrand === 'mastercard' && (
                    <Icon name='MasterCard' variant='xl' color='white' />
                  )}
                  {paymentCardBrand === 'amex' && (
                    <Icon name='AmericanExpress' variant='xl' color='white' />
                  )}
                  {paymentCardBrand === 'discover' && (
                    <Icon name='Discover' variant='xl' color='white' />
                  )}
                  <Text
                    variant='webBodySecondary'
                    color='textSecondary'
                    ml='xxs'>
                    {'**** **** **** ' +
                      team?.subscriptionDetails?.paymentCardLast4}
                  </Text>
                </Box>
              ) : (
                <Text variant='webBodySecondary' color='textSecondary' ml='xxs'>
                  -
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Tab.Navigator
        screenOptions={{
          swipeEnabled: false,
        }}
        sceneContainerStyle={{
          backgroundColor: 'transparent',
        }}
        initialRouteName='TeamMembers'
        backBehavior='none'
        tabBar={(props) => (
          <Box
            flexDirection='row'
            alignItems='center'
            paddingHorizontal='l'
            paddingBottom='xs'
            maxHeight={50}>
            <CustomTabBar {...props} spacing={theme.spacing.l} />
          </Box>
        )}>
        <Tab.Screen
          name='TeamMembers'
          options={{
            tabBarLabel: `${t('models:teams.subscriptions.members')} (${
              team?.usersCount ?? 0
            })`,
          }}
          children={() => (
            <Box flex={1} mt='xs'>
              <Box
                flexDirection='row'
                mb='xs'
                mx='l'
                justifyContent='space-between'>
                <PopupTeamMemberRoleButtonweb
                  initialRoles={teamRoles}
                  boxProps={offsetPopButton}
                  onPressRole={(role) => {
                    setSelectedRole(role.key);
                    resetMembers(role.key);
                  }}
                />
                <Box flexDirection='row'>
                  {!isPersonal && (
                    <Button
                      marginTop='none'
                      disabled={isExpired || !team?.subscriptionDetails}
                      variant='create'
                      isSmall
                      marginEnd='xs'
                      height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                      backgroundColor='black'
                      onPress={() => {
                        handleInviteToTeamPress();
                      }}
                      prefix={
                        <Icon
                          variant='s'
                          name='Paperclip'
                          color={isExpired ? 'grey04' : 'white'}
                        />
                      }
                      float={undefined}>
                      Copy to Invite
                    </Button>
                  )}
                  {canRemoveMember && (
                    <>
                      <Button
                        marginTop='none'
                        disabled={false}
                        variant='create'
                        isSmall
                        marginEnd='xs'
                        height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
                        backgroundColor='black'
                        onPress={() => {
                          navigation.navigate('add-team-members', {
                            teamUUId: team.uuid,
                            initialUsers: teamUsers,
                          });
                        }}
                        prefix={
                          <Icon
                            variant='s'
                            name='Plus'
                            color={isExpired ? 'grey04' : 'white'}
                          />
                        }
                        float={undefined}>
                        Add Members
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
              <FlatList
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
                data={displayMembers}
                renderItem={({ item }) => (
                  <Box mx='l'>{renderMemberItem(item)}</Box>
                )}
                ListHeaderComponent={() => (
                  <Box mx='l'>{renderMembersHeader()}</Box>
                )}
              />
            </Box>
          )}
        />
        <Tab.Screen
          name='TeamInvoice'
          options={{
            tabBarLabel: `${t(
              'models:teams.subscriptions.invoices.invoice'
            )} (${team?.invoices?.length ?? 0})`,
          }}
          children={() => (
            <Box flex={1} position='relative' mt='xs'>
              <FlatList
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
                data={displayInvoices}
                renderItem={({ item }) => <Box mx='l'>{renderItem(item)}</Box>}
                ListHeaderComponent={() => <Box mx='l'>{renderHeader()}</Box>}
              />
              {isRefreshingInvoices && (
                <Box
                  position='absolute'
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  zIndex={1}
                  backgroundColor='white'
                  opacity={0.9}
                  alignItems='center'
                  justifyContent='center'>
                  <ActivityIndicatorLoading />
                  <Text variant='labelSmall' color='textSecondary' mt='xs'>
                    Refreshing invoice data...
                  </Text>
                </Box>
              )}
            </Box>
          )}
        />
      </Tab.Navigator>

      {isExpired && (
        <Box style={styles.bannerContainer}>
          <TeamExpiryBanner
            planName={team?.name ?? 'Team'}
            expiredDate={
              team?.subscriptionDetails?.expirationDate
                ? new Date(team?.subscriptionDetails?.expirationDate)
                : undefined
            }
            onUpgradeNow={() => {
              goToUpgradeSubscription();
            }}
            isOwnerOrAdmin={
              team?.currentUserRole === TeamMemberRole.Owner ||
              team?.currentUserRole === TeamMemberRole.Admin ||
              team?.owner?.id === me?.id
            }
            teamId={team?.id ?? undefined}
            teamOwnerId={team?.owner?.id ?? undefined}
            teamOwnerName={team?.owner?.name ?? undefined}
          />
        </Box>
      )}

      {isDeleteModalVisible && (
        <ConfirmModal
          showModal={isDeleteModalVisible}
          onClose={() => setDeleteModalVisible(false)}
          onPress={() =>
            selectedMember && removeMember && removeMember(selectedMember)
          }
          buttonText={t('models:teams.removeTeamMember.remove')}
          title={t('models:teams.removeTeamMember.title')}
          message={t('models:teams.removeTeamMember.message', {
            member: selectedMember
              ? selectedMember.firstName + ' ' + selectedMember.lastName
              : '',
          })}
        />
      )}
      <Modal animationType='fade' transparent={true} visible={modalOpen}>
        <Box style={styles.modalContainer}>
          <TouchableWithoutFeedback
            onPress={() => setModalOpen(!modalOpen)}
            style={styles.modal}
            accessibilityLabel='CloseModalButton'>
            <Box style={styles.modal} />
          </TouchableWithoutFeedback>

          {selectableTeamRoles && (
            <ShadowBox variant='thin' style={styles.popUpWeb}>
              <Box
                marginVertical='m'
                marginHorizontal='m'
                flex={1}
                justifyContent='space-between'>
                <Box
                  style={styles.modalHeader}
                  accessibilityLabel='RoleSelectModal'>
                  <ModalHeader
                    title={t('models:projects.roles.selectRole')}
                    submitLabel='Cancel'
                    onCancel={() => setModalOpen(false)}
                    showCancel={false}
                    onSubmit={() => setModalOpen(false)}
                  />
                </Box>
                {selectableTeamRoles.map((i, index) => {
                  return (
                    <TouchableOpacity
                      key={i}
                      onPress={() => {
                        selectedMember &&
                          updateTeamMember?.(selectedMember?.id, i);
                        setModalOpen(false);
                      }}
                      accessibilityLabel={i}>
                      <Box
                        flexDirection='row'
                        justifyContent='space-between'
                        alignItems='center'
                        marginBottom={
                          index !== selectableTeamRoles.length - 1
                            ? 'l'
                            : undefined
                        }>
                        <Text
                          variant='body'
                          style={{ textTransform: 'capitalize' }}>
                          {i.toLowerCase()}
                        </Text>
                        {selectedMember?.teamRoles?.includes(
                          i.toLocaleUpperCase() as TeamRole
                        ) && (
                          <Icon
                            variant='s'
                            name='Check'
                            color='greenSecondary'
                            accessibilityLabel={`SelectedRole-${i}`}
                          />
                        )}
                      </Box>
                    </TouchableOpacity>
                  );
                })}

                {canRemoveMember && (
                  <>
                    <Box
                      backgroundColor='grey02'
                      height={1}
                      marginVertical='m'
                    />
                    <TouchableOpacity
                      onPress={() => {
                        setModalOpen(false);
                        setDeleteModalVisible(true);
                      }}
                      accessibilityLabel='Remove'>
                      <Text variant='body' color='alertRed'>
                        Remove
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </Box>
            </ShadowBox>
          )}
        </Box>
      </Modal>

      <WebModal
        onClose={() => setShowAddMembersModal(false)}
        visible={showAddMembersModal}
        width={504}
        height={665}
        accessibilityLabel='add-members'>
        <Formik
          initialValues={initialValues}
          onSubmit={() => {}}
          validationSchema={projectFormSchema}>
          {({ setFieldValue, values, isSubmitting }) => {
            const { selectedUserIds: selectedUserIdsRaw, users } = values;
            const selectedUserIds = selectedUserIdsRaw?.filter(
              (s) => !!s
            ) as string[];
            const ButtonAdd = (
              xWidth: number,
              accessibilityLabel: string,
              backgroundColor: keyof typeof theme.colors,
              txtColor: keyof typeof theme.colors,
              isLoading: boolean,
              onPress: () => void
            ) => {
              return (
                <TouchableOpacity
                  testID='add-member-button'
                  onPress={onPress}
                  accessibilityLabel={accessibilityLabel}>
                  <Box
                    style={{ width: xWidth, height: 48 }}
                    alignItems='center'
                    borderWidth={1}
                    borderColor={txtColor}
                    borderRadius='xs'
                    justifyContent='center'
                    backgroundColor={backgroundColor}
                    mr='xs'>
                    {!isLoading && (
                      <Text variant='labelSmall' color={txtColor}>
                        {accessibilityLabel}
                      </Text>
                    )}
                    {isLoading && <ActivityIndicatorLoading />}
                  </Box>
                </TouchableOpacity>
              );
            };
            return (
              <>
                <Box
                  flexDirection='row'
                  justifyContent='space-between'
                  marginHorizontal='m'
                  mt='l'
                  mb='xs'>
                  <Text variant='labelEmphasized'>
                    {t('models:profile.subscriptionPlans.inviteUser')}
                  </Text>
                  <Icon
                    name='X'
                    variant='m'
                    color='black'
                    onPress={() => {
                      setShowAddMembersModal(false);
                    }}
                  />
                </Box>
                <Box
                  flex={1}
                  accessibilityLabel='Add Members'
                  backgroundColor='white'>
                  <SelectMembersList
                    isCreateTask={false}
                    isCreateProject={false}
                    teamUUId={teamUUId}
                    projectUUId=''
                    contacts={contacts.filter(
                      (c) => c.email !== TASKTAG_SUPPORT_EMAIL
                    )}
                    showTaskTagUsers
                    selectedUserIds={selectedUserIds}
                    disabledUsersId={selectedUserIds}
                    users={users}
                    updateUsers={(newUsers: AddMemberType[]) => {
                      setFieldValue(
                        'users',
                        newUsers.map((u) => {
                          return {
                            ...u,
                            role: u.role || defaultTeamRole,
                          };
                        })
                      );
                    }}
                  />
                  <Box style={styles.bottomButtonWeb} flexDirection='row'>
                    {ButtonAdd(
                      77,
                      t('shared:back'),
                      'white',
                      'black',
                      false,
                      () => {
                        setShowAddMembersModal(false);
                      }
                    )}
                    {ButtonAdd(
                      107,
                      t('models:invite.title'),
                      users.length > 0 ? 'black' : 'grey02',
                      'white',
                      isSubmitting,
                      async () => {
                        if (users.length > 0) {
                          goToUpgradeSubscriptionWithInvite();
                          setTeamValues({ teamValues, users: users });
                          setShowAddMembersModal(false);
                        }
                      }
                    )}
                  </Box>
                </Box>
              </>
            );
          }}
        </Formik>
      </WebModal>
      <Toast ref={toastRef} />
    </Box>
  );
};

const styles = StyleSheet.create({
  bottomButtonWeb: {
    position: 'absolute',
    bottom: theme.spacing.m,
    right: theme.spacing.xs,
  },
  modal: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
    opacity: 0.5,
  },
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalHeader: {
    paddingBottom: theme.spacing.xl,
    paddingTop: theme.spacing.l,
  },
  popUp: {
    position: 'absolute',
    backgroundColor: 'white',
    width: '100%',
    bottom: 0,
    paddingBottom: theme.spacing.xxl,
    borderTopLeftRadius: theme.borderRadii.l,
    borderTopRightRadius: theme.borderRadii.l,
  },
  popUpWeb: {
    position: 'absolute',
    backgroundColor: 'white',
    width: 300,
    paddingBottom: theme.spacing.l,
    borderRadius: theme.borderRadii.s,
  },
  bannerContainer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    zIndex: 100,
  },
});
